import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity, TextInput, Modal, useWindowDimensions } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
    FaArrowLeft, FaTwitter, FaTelegram, FaTiktok, FaInstagram,
    FaHeart, FaComment, FaShare, FaEye, FaChartLine, FaTrophy,
    FaLightbulb, FaCheckCircle, FaExclamationTriangle,
    FaBell, FaBellSlash, FaTimes, FaFacebookF,
} from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import { useTheme } from '../theme/ThemeContext';

let THEME = {};

const buildCompareTheme = (theme) => {
    const isDark = theme.bg === '#050507' || (theme.bg && theme.bg.startsWith('#0'));
    return isDark ? {
        primary: '#38bdf8',
        primaryGlow: 'transparent',
        success: '#4ade80',
        warning: '#fbbf24',
        error: '#f87171',
        purple: '#a78bfa',
        bg: '#0f1117',
        surface: '#22252f',
        card: '#1a1d27',
        cardBorder: '#2e3140',
        textMain: '#f1f5f9',
        textSecondary: '#94a3b8',
        textTertiary: '#64748b',
        bgSurface: '#22252f',
        bgElevated: '#22252f',
        inputBg: '#22252f',
        modalBg: '#1a1d27',
        overlay: 'rgba(0,0,0,0.3)',
    } : {
        primary: '#0288d1',
        primaryGlow: 'transparent',
        success: '#16a34a',
        warning: '#d97706',
        error: '#dc2626',
        purple: '#7c3aed',
        bg: '#ffffff',
        surface: '#f9fafb',
        card: '#ffffff',
        cardBorder: '#e5e7eb',
        textMain: '#111827',
        textSecondary: '#374151',
        textTertiary: '#9ca3af',
        bgSurface: '#f9fafb',
        bgElevated: '#f9fafb',
        inputBg: '#f9fafb',
        modalBg: '#ffffff',
        overlay: 'rgba(0,0,0,0.3)',
    };
};

const PLATFORM_COLORS = {
    twitter: '#1da1f2',
    telegram: '#0088cc',
    tiktok: '#ff0050',
    instagram: '#e1306c',
    facebook_group: '#1877F2',
};

const PLATFORM_ICONS = {
    twitter: FaTwitter,
    telegram: FaTelegram,
    tiktok: FaTiktok,
    instagram: FaInstagram,
    facebook_group: FaFacebookF,
};

// Palette for distinguishing campaigns
const CAMPAIGN_PALETTE = ['#30D158', '#3ea6ff', '#bf5af2', '#FF9F0A', '#FF453A'];

const formatNum = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
};

export const CampaignComparePanel = ({ campaignIds: propIds, onBack } = {}) => {
    const { theme: globalTheme } = useTheme();
    Object.assign(THEME, buildCompareTheme(globalTheme));
    styles = getStyles();
    const { t } = useTranslation();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { width } = useWindowDimensions();
    const isDesktop = width >= 1024;
    const isRTL = i18n.language === 'he';

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Metric mode toggle
    const [metricMode, setMetricMode] = useState('total'); // 'total' | 'average'

    // Watch state
    const [existingWatch, setExistingWatch] = useState(null);   // watch object if already watching
    const [watchModalVisible, setWatchModalVisible] = useState(false);
    const [watchNote, setWatchNote] = useState('');
    const [watchLoading, setWatchLoading] = useState(false);

    const ids = propIds || (searchParams.get('ids') || '').split(',').filter(Boolean);

    const fetchComparison = useCallback(async () => {
        if (ids.length < 2) {
            setError(t('app.campaigns.chooseAtLeast2', { count: ids.length }));
            setLoading(false);
            return;
        }
        try {
            setError(null);
            const result = await ListsManager.compareCampaigns(ids);
            if (!result) throw new Error('No data returned');
            setData(result);
        } catch (e) {
            console.error('Compare fetch error:', e);
            setError(t('app.campaigns.failedToLoadComparison'));
        } finally {
            setLoading(false);
        }
    }, [ids.join(',')]);

    const checkWatchStatus = useCallback(async () => {
        if (ids.length < 2) return;
        try {
            const watches = await ListsManager.getComparisonWatches();
            const sortedIds = [...ids].sort();
            const found = (watches || []).find(w => {
                const wIds = [...(w.campaign_ids || [])].sort();
                return wIds.length === sortedIds.length && wIds.every((id, i) => id === sortedIds[i]);
            });
            setExistingWatch(found || null);
        } catch (_) {
            // non-fatal
        }
    }, [ids.join(',')]);

    useEffect(() => { fetchComparison(); }, [fetchComparison]);
    useEffect(() => { checkWatchStatus(); }, [checkWatchStatus]);

    const handleAddWatch = async () => {
        if (!data) return;
        setWatchLoading(true);
        try {
            const campaignNames = data.campaigns.map(c => c.name);
            const result = await ListsManager.addComparisonWatch(ids, campaignNames, watchNote.trim() || null);
            if (result) {
                setExistingWatch(result);
                setWatchModalVisible(false);
                setWatchNote('');
            }
        } catch (_) {
            // non-fatal
        } finally {
            setWatchLoading(false);
        }
    };

    const handleRemoveWatch = async () => {
        if (!existingWatch) return;
        setWatchLoading(true);
        try {
            const ok = await ListsManager.removeComparisonWatch(existingWatch.id);
            if (ok) setExistingWatch(null);
        } catch (_) {
            // non-fatal
        } finally {
            setWatchLoading(false);
        }
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={THEME.primary} />
                <Text style={styles.loadingText}>{t('app.campaigns.comparingWithAI')}</Text>
                <Text style={styles.loadingSubtext}>{t('app.campaigns.mayTakeSeconds')}</Text>
            </View>
        );
    }

    if (error || !data) {
        return (
            <View style={styles.loadingContainer}>
                <Text style={styles.errorText}>{error || t('app.common.somethingWentWrong')}</Text>
                <TouchableOpacity style={styles.retryButton} onPress={() => onBack ? onBack() : navigate('/campaigns')}>
                    <Text style={styles.retryText}>{t('app.campaigns.backToCampaigns')}</Text>
                </TouchableOpacity>
            </View>
        );
    }

    const campaigns = data.campaigns || [];
    const ai = data.ai_comparison;
    const winnerIdx = ai ? campaigns.findIndex(c => c.name === ai.overall_winner) : -1;
    const winnerColor = winnerIdx >= 0 ? CAMPAIGN_PALETTE[winnerIdx] : '#FFD60A';

    // Compute max values for bar scaling
    const maxLikes = Math.max(...campaigns.map(c => c.stats?.total_likes || 0), 1);
    const maxComments = Math.max(...campaigns.map(c => c.stats?.total_comments || 0), 1);
    const maxShares = Math.max(...campaigns.map(c => c.stats?.total_shares || 0), 1);
    const maxViews = Math.max(...campaigns.map(c => c.stats?.total_views || 0), 1);
    const maxPosts = Math.max(...campaigns.map(c => c.stats?.total_posts || 0), 1);

    const metrics = [
        { label: t('app.campaigns.posts'), key: 'total_posts', max: maxPosts, icon: <FaChartLine size={13} color={THEME.primary} />, color: THEME.primary },
        { label: t('app.campaigns.likes'), key: 'total_likes', max: maxLikes, icon: <FaHeart size={13} color="#ff453a" />, color: '#ff453a' },
        { label: t('app.campaigns.comments'), key: 'total_comments', max: maxComments, icon: <FaComment size={13} color="#3ea6ff" />, color: '#3ea6ff' },
        { label: t('app.campaigns.shares'), key: 'total_shares', max: maxShares, icon: <FaShare size={13} color={THEME.success} />, color: THEME.success },
        { label: t('app.campaigns.views'), key: 'total_views', max: maxViews, icon: <FaEye size={13} color={THEME.purple} />, color: THEME.purple },
    ];

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
            <style dangerouslySetInnerHTML={{ __html: `
                * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
                .mono-value { font-family: 'SF Mono', 'Menlo', monospace; }
                .cmp-fade { }
                .cmp-fade-1 { }
                .cmp-fade-2 { }
                .cmp-fade-3 { }
                .cmp-fade-4 { }
                .cmp-fade-5 { }
            `}} />

            {/* Back header */}
            <div className="cmp-fade cmp-fade-1">
            <View style={styles.header}>
                <TouchableOpacity style={styles.backBtn} onPress={() => onBack ? onBack() : navigate('/campaigns')}>
                    <FaArrowLeft size={16} color={THEME.textSecondary} />
                </TouchableOpacity>
                <View style={{ flex: 1 }}>
                    <Text style={styles.pageTitle}>{t('app.campaigns.campaignComparison')}</Text>
                    <Text style={styles.pageSubtitle}>{t('app.campaigns.campaignComparisonSubtitle', { count: campaigns.length })}</Text>
                </View>

                {/* Watch button */}
                {existingWatch ? (
                    <View style={styles.watchingBadge}>
                        <FaBell size={13} color={THEME.success} />
                        <Text style={styles.watchingBadgeText}>{t('app.campaigns.watching')}</Text>
                        <TouchableOpacity
                            onPress={handleRemoveWatch}
                            disabled={watchLoading}
                            style={styles.watchingRemoveBtn}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                            {watchLoading
                                ? <ActivityIndicator size="small" color={THEME.textTertiary} style={{ width: 12, height: 12 }} />
                                : <FaTimes size={11} color={THEME.textTertiary} />
                            }
                        </TouchableOpacity>
                    </View>
                ) : (
                    <TouchableOpacity
                        style={styles.watchBtn}
                        onPress={() => setWatchModalVisible(true)}
                        disabled={watchLoading}
                    >
                        <FaBell size={13} color={THEME.textSecondary} />
                        <Text style={styles.watchBtnText}>{t('app.campaigns.watch')}</Text>
                    </TouchableOpacity>
                )}
            </View>
            </div>

            {/* Watch Modal */}
            <Modal
                visible={watchModalVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setWatchModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.watchModal}>
                        {/* Modal header */}
                        <View style={styles.watchModalHeader}>
                            <View style={styles.watchModalIconWrap}>
                                <FaBell size={20} color={THEME.primary} />
                            </View>
                            <TouchableOpacity
                                style={styles.watchModalCloseBtn}
                                onPress={() => { setWatchModalVisible(false); setWatchNote(''); }}
                            >
                                <FaTimes size={16} color={THEME.textTertiary} />
                            </TouchableOpacity>
                        </View>

                        <Text style={styles.watchModalTitle}>{t('app.campaigns.watchThisComparison')}</Text>
                        <Text style={styles.watchModalSubtitle}>
                            The action queue AI will monitor these campaigns and alert you when there's something worth acting on.
                        </Text>

                        {/* Campaign pills */}
                        <View style={styles.watchCampaignPills}>
                            {campaigns.map((c, i) => (
                                <View key={c.id} style={[styles.watchCampaignPill, { borderColor: CAMPAIGN_PALETTE[i] + '60' }]}>
                                    <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: CAMPAIGN_PALETTE[i] }} />
                                    <Text style={styles.watchCampaignPillText} numberOfLines={1}>{c.name}</Text>
                                </View>
                            ))}
                        </View>

                        {/* Note input */}
                        <Text style={styles.watchNoteLabel}>What should the AI watch for? <Text style={{ color: THEME.textTertiary }}>(optional)</Text></Text>
                        <TextInput
                            style={styles.watchNoteInput}
                            placeholder="e.g. Our Q1 launch vs competitor rebrand — watch for engagement gap"
                            placeholderTextColor={THEME.textTertiary}
                            value={watchNote}
                            onChangeText={setWatchNote}
                            multiline
                            numberOfLines={3}
                            maxLength={300}
                        />
                        <Text style={styles.watchNoteCount}>{watchNote.length}/300</Text>

                        {/* Actions */}
                        <View style={styles.watchModalActions}>
                            <TouchableOpacity
                                style={styles.watchCancelBtn}
                                onPress={() => { setWatchModalVisible(false); setWatchNote(''); }}
                            >
                                <Text style={styles.watchCancelBtnText}>{t('app.campaigns.cancel')}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.watchConfirmBtn, watchLoading && { opacity: 0.6 }]}
                                onPress={handleAddWatch}
                                disabled={watchLoading}
                            >
                                {watchLoading
                                    ? <ActivityIndicator size="small" color="#000" />
                                    : <>
                                        <FaBell size={13} color="#000" />
                                        <Text style={styles.watchConfirmBtnText}>{t('app.campaigns.startWatching')}</Text>
                                    </>
                                }
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>

            {/* Campaign header cards */}
            <div className="cmp-fade cmp-fade-1">
            <View style={[styles.campaignHeaderRow, !isDesktop && { flexWrap: 'wrap' }]}>
                {campaigns.map((c, i) => (
                    <View
                        key={c.id}
                        style={[
                            styles.campaignHeaderCard,
                            { borderTopWidth: 3, borderTopColor: CAMPAIGN_PALETTE[i] },
                        ]}
                    >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                            <View style={{
                                width: 12, height: 12, borderRadius: 6,
                                backgroundColor: CAMPAIGN_PALETTE[i],
                            }} />
                            <Text style={styles.campaignHeaderName} numberOfLines={1}>{c.name}</Text>
                        </View>
                        {c.description && (
                            <Text style={styles.campaignHeaderDesc} numberOfLines={2}>{c.description}</Text>
                        )}
                        <View style={[styles.statusPill, { backgroundColor: c.status === 'active' ? THEME.success + '18' : THEME.warning + '18' }]}>
                            <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.status === 'active' ? THEME.success : THEME.warning }} />
                            <Text style={{ color: c.status === 'active' ? THEME.success : THEME.warning, fontSize: 11, fontWeight: '700', textTransform: 'uppercase' }}>
                                {c.status}
                            </Text>
                        </View>
                    </View>
                ))}
            </View>
            </div>

            {/* AI Analysis — Visual Scorecard */}
            {ai ? (
                <div className="cmp-fade cmp-fade-2">
                <View style={[styles.aiWrapper, isRTL && { direction: 'rtl' }]}>
                    {/* Section Header */}
                    <Text style={styles.sectionTitle}>{t('app.campaigns.aiInsights')}</Text>

                    {/* Winner Banner */}
                    <View style={[styles.aiWinnerBanner, { backgroundColor: THEME.bgSurface }]}>
                        <View style={[styles.aiWinnerTrophyWrap, { backgroundColor: winnerColor + '20', borderColor: winnerColor + '30' }]}>
                            <FaTrophy size={18} color="#FFD60A" />
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.aiWinnerLabel}>{t('app.campaigns.overallWinner')}</Text>
                            <Text style={[styles.aiWinnerName, { color: winnerColor }]}>{ai.overall_winner}</Text>
                        </View>
                    </View>

                    {/* Summary */}
                    <View style={styles.aiSummaryBlock}>
                        <Text style={styles.aiSummaryText}>{ai.summary}</Text>
                    </View>

                    {/* Key Differences */}
                    {ai.key_differences?.length > 0 && (
                        <View style={{ gap: 8 }}>
                            <Text style={styles.aiSubsectionTitle}>{t('app.campaigns.keyDifferences')}</Text>
                            {ai.key_differences.map((diff, i) => (
                                <View key={i} style={styles.aiDiffChip}>
                                    <View style={[styles.aiDiffAccent, { backgroundColor: THEME.warning }]} />
                                    <Text style={styles.aiDiffText}>{diff}</Text>
                                </View>
                            ))}
                        </View>
                    )}

                    {/* Strengths Grid */}
                    {ai.strengths && ai.strengths.length > 0 && (
                        <View style={{ gap: 10 }}>
                            <Text style={styles.aiSubsectionTitle}>{t('app.campaigns.strengths')}</Text>
                            <View style={[{ gap: 10 }, isDesktop && { flexDirection: 'row', flexWrap: 'wrap' }]}>
                                {ai.strengths.map((entry, i) => {
                                    const campName = entry.campaign_name || entry[0] || '';
                                    const points = entry.points || entry[1] || [];
                                    const color = CAMPAIGN_PALETTE[i] || THEME.primary;
                                    return (
                                        <View key={campName} style={[styles.aiStrengthCard, isDesktop && { flex: 1, minWidth: 200 }]}>
                                            <View style={[styles.aiStrengthHeader, { backgroundColor: color + '12' }]}>
                                                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
                                                <Text style={styles.aiStrengthName}>{campName}</Text>
                                            </View>
                                            <View style={{ padding: 14, gap: 6 }}>
                                                {(points || []).map((s, si) => (
                                                    <View key={si} style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                                                        <Text style={{ color, fontSize: 13, lineHeight: 20, flexShrink: 0 }}>✓</Text>
                                                        <Text style={styles.aiStrengthPoint}>{s}</Text>
                                                    </View>
                                                ))}
                                            </View>
                                        </View>
                                    );
                                })}
                            </View>
                        </View>
                    )}

                    {/* Recommendations */}
                    {ai.recommendations?.length > 0 && (
                        <View style={{ gap: 10 }}>
                            <Text style={styles.aiSubsectionTitle}>{t('app.campaigns.recommendations')}</Text>
                            <View style={{ gap: 8 }}>
                                {ai.recommendations.map((rec, i) => (
                                    <View key={i} style={styles.aiRecCard}>
                                        <View style={styles.aiRecBadge}>
                                            <Text style={styles.aiRecBadgeText}>{i + 1}</Text>
                                        </View>
                                        <Text style={styles.aiRecText}>{rec}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    )}
                </View>
                </div>
            ) : (
                <div className="cmp-fade cmp-fade-2">
                <View style={[styles.sectionCard, { alignItems: 'center', paddingVertical: 32 }]}>
                    <FaChartLine size={28} color={THEME.textTertiary} />
                    <Text style={{ color: THEME.textSecondary, fontSize: 14, marginTop: 12 }}>{t('app.campaigns.aiAnalysisUnavailable')}</Text>
                </View>
                </div>
            )}

            {/* Metric Comparison */}
            <div className="cmp-fade cmp-fade-3">
            <View style={styles.sectionCard}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>{t('app.campaigns.engagementComparison')}</Text>
                    <View style={styles.modeToggle}>
                        <TouchableOpacity
                            style={[styles.modeToggleBtn, metricMode === 'total' && styles.modeToggleBtnActive]}
                            onPress={() => setMetricMode('total')}
                        >
                            <Text style={[styles.modeToggleBtnText, metricMode === 'total' && styles.modeToggleBtnTextActive]}>{t('app.campaigns.total')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.modeToggleBtn, metricMode === 'average' && styles.modeToggleBtnActive]}
                            onPress={() => setMetricMode('average')}
                        >
                            <Text style={[styles.modeToggleBtnText, metricMode === 'average' && styles.modeToggleBtnTextActive]}>{t('app.campaigns.avgPerPost')}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
                <View style={{ gap: 18 }}>
                    {metrics.map((m) => {
                        const isPostsMetric = m.key === 'total_posts';
                        const vals = campaigns.map(c => {
                            const raw = c.stats?.[m.key] || 0;
                            if (metricMode === 'average' && !isPostsMetric) {
                                return raw / Math.max(c.stats?.total_posts || 1, 1);
                            }
                            return raw;
                        });
                        const maxVal = Math.max(...vals, 1);
                        const winnerIdx = vals.filter(v => v === maxVal).length === 1 ? vals.indexOf(maxVal) : -1;
                        return (
                            <View key={m.key}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    {m.icon}
                                    <Text style={styles.metricLabel}>{m.label}</Text>
                                    {metricMode === 'average' && !isPostsMetric && (
                                        <Text style={{ color: THEME.textTertiary, fontSize: 11, fontWeight: '500' }}>per post</Text>
                                    )}
                                </View>
                                <View style={{ gap: 8 }}>
                                    {campaigns.map((c, i) => {
                                        const val = vals[i];
                                        const pct = Math.max((val / maxVal) * 100, val > 0 ? 4 : 0);
                                        const isWinner = i === winnerIdx && val > 0;
                                        return (
                                            <View key={c.id} style={[
                                                { flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 8, paddingVertical: 3, paddingHorizontal: 4 },
                                                isWinner && { backgroundColor: CAMPAIGN_PALETTE[i] + '0F' },
                                            ]}>
                                                <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: CAMPAIGN_PALETTE[i], flexShrink: 0 }} />
                                                <Text style={[styles.metricCampaignName, isWinner && { color: THEME.textMain, fontWeight: '700' }]} numberOfLines={1}>{c.name}</Text>
                                                <View style={styles.barTrack}>
                                                    <View style={[
                                                        styles.barFill,
                                                        { width: `${pct}%`, backgroundColor: CAMPAIGN_PALETTE[i] },
                                                    ]} />
                                                </View>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, minWidth: 60, justifyContent: 'flex-end' }}>
                                                    {isWinner && <FaTrophy size={11} color="#FFD60A" />}
                                                    <span className="mono-value" style={{ color: isWinner ? THEME.textMain : THEME.textSecondary, fontSize: 13, fontWeight: isWinner ? '700' : '600', textAlign: 'right' }}>
                                                        {metricMode === 'average' && !isPostsMetric ? val.toFixed(1) : formatNum(val)}
                                                    </span>
                                                </View>
                                            </View>
                                        );
                                    })}
                                </View>
                            </View>
                        );
                    })}
                </View>
            </View>
            </div>

            {/* Platform Breakdown */}
            <div className="cmp-fade cmp-fade-4">
            <View style={styles.sectionCard}>
                <Text style={styles.sectionTitle}>{t('app.campaigns.platformBreakdown')}</Text>
                <View style={[{ gap: 16 }, isDesktop && { flexDirection: 'row', flexWrap: 'wrap' }]}>
                    {campaigns.map((c, i) => (
                        <View key={c.id} style={[styles.platformBreakdownCard, isDesktop && { flex: 1, minWidth: 180 }]}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: CAMPAIGN_PALETTE[i] }} />
                                <Text style={{ color: THEME.textMain, fontSize: 14, fontWeight: '700' }} numberOfLines={1}>{c.name}</Text>
                            </View>
                            {(c.platform_breakdown || []).length === 0 ? (
                                <Text style={{ color: THEME.textTertiary, fontSize: 13 }}>{t('app.campaigns.noPostsYet')}</Text>
                            ) : (
                                (c.platform_breakdown || []).map((p) => {
                                    const PIcon = PLATFORM_ICONS[p.platform] || FaTwitter;
                                    const pColor = PLATFORM_COLORS[p.platform] || '#888';
                                    return (
                                        <View key={p.platform} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                            <PIcon size={13} color={pColor} />
                                            <Text style={{ color: THEME.textSecondary, fontSize: 13, flex: 1 }}>{p.platform}</Text>
                                            <span className="mono-value" style={{ color: THEME.textMain, fontSize: 13 }}>{p.post_count} posts</span>
                                            <span className="mono-value" style={{ color: THEME.textTertiary, fontSize: 12 }}>{formatNum(p.views)}v</span>
                                        </View>
                                    );
                                })
                            )}
                        </View>
                    ))}
                </View>
            </View>
            </div>

            {/* Comment Sentiment */}
            {campaigns.some(c => c.comment_sentiment) && (
                <div className="cmp-fade cmp-fade-4">
                <View style={styles.sectionCard}>
                    <Text style={styles.sectionTitle}>{t('app.campaigns.commentSentiment')}</Text>
                    <View style={{ gap: 16 }}>
                        {campaigns.map((c, i) => {
                            const sent = c.comment_sentiment;
                            if (!sent) return (
                                <View key={c.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: CAMPAIGN_PALETTE[i] }} />
                                    <Text style={{ color: THEME.textMain, fontSize: 14, fontWeight: '600', flex: 1 }}>{c.name}</Text>
                                    <Text style={{ color: THEME.textTertiary, fontSize: 13 }}>{t('app.campaigns.noCommentData')}</Text>
                                </View>
                            );
                            const posPct = sent.positive_pct || 0;
                            const negPct = sent.negative_pct || 0;
                            const neuPct = Math.max(0, 100 - posPct - negPct);
                            return (
                                <View key={c.id} style={{ gap: 8 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: CAMPAIGN_PALETTE[i] }} />
                                        <Text style={{ color: THEME.textMain, fontSize: 14, fontWeight: '600', flex: 1 }}>{c.name}</Text>
                                        <Text style={{ color: THEME.textTertiary, fontSize: 12 }}>{sent.total_analyzed?.toLocaleString()} analyzed</Text>
                                    </View>
                                    <View style={{ flexDirection: 'row', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
                                        {posPct > 0 && <View style={{ flex: posPct, backgroundColor: THEME.success }} />}
                                        {neuPct > 0 && <View style={{ flex: neuPct, backgroundColor: THEME.textTertiary }} />}
                                        {negPct > 0 && <View style={{ flex: negPct, backgroundColor: THEME.error }} />}
                                    </View>
                                    <View style={{ flexDirection: 'row', gap: 16 }}>
                                        <Text style={styles.sentimentLabel}><span style={{ color: THEME.success }}>●</span> {posPct.toFixed(0)}% {t('app.campaigns.sentPos')}</Text>
                                        <Text style={styles.sentimentLabel}><span style={{ color: THEME.textTertiary }}>●</span> {neuPct.toFixed(0)}% {t('app.campaigns.sentNeu')}</Text>
                                        <Text style={styles.sentimentLabel}><span style={{ color: THEME.error }}>●</span> {negPct.toFixed(0)}% {t('app.campaigns.sentNeg')}</Text>
                                        {sent.avg_sentiment != null && (
                                            <Text style={[styles.sentimentLabel, { marginLeft: 'auto', color: sent.avg_sentiment >= 0 ? THEME.success : THEME.error }]}>
                                                avg {sent.avg_sentiment >= 0 ? '+' : ''}{sent.avg_sentiment?.toFixed(2)}
                                            </Text>
                                        )}
                                    </View>
                                </View>
                            );
                        })}
                    </View>
                </View>
                </div>
            )}

            {/* Top Posts per campaign */}
            <div className="cmp-fade cmp-fade-5">
            <View style={styles.sectionCard}>
                <Text style={styles.sectionTitle}>{t('app.campaigns.topPosts')}</Text>
                <View style={[{ gap: 20 }, isDesktop && { flexDirection: 'row', flexWrap: 'wrap' }]}>
                    {campaigns.map((c, i) => (
                        <View key={c.id} style={[styles.topPostsColumn, isDesktop && { flex: 1, minWidth: 200 }]}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: CAMPAIGN_PALETTE[i] }} />
                                <Text style={{ color: THEME.textMain, fontSize: 14, fontWeight: '700' }} numberOfLines={1}>{c.name}</Text>
                            </View>
                            {(c.top_posts || []).length === 0 ? (
                                <Text style={{ color: THEME.textTertiary, fontSize: 13 }}>{t('app.campaigns.noPostsYet')}</Text>
                            ) : (
                                (c.top_posts || []).slice(0, 3).map((post, pi) => {
                                    const PIcon = PLATFORM_ICONS[post.platform] || FaTwitter;
                                    const pColor = PLATFORM_COLORS[post.platform] || '#888';
                                    return (
                                        <View key={post.id || pi} style={styles.topPostItem}>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                <PIcon size={12} color={pColor} />
                                                <Text style={{ color: THEME.textSecondary, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                    {post.platform}
                                                </Text>
                                                <View style={{ marginLeft: 'auto' }}>
                                                    <span className="mono-value" style={{ color: THEME.textTertiary, fontSize: 11 }}>
                                                        {formatNum(post.total_engagement)} eng
                                                    </span>
                                                </View>
                                            </View>
                                            {post.content && (
                                                <Text style={styles.topPostContent} numberOfLines={3}>{post.content}</Text>
                                            )}
                                            {post.video_analysis_summary && (
                                                <View style={styles.videoAnalysisBadge}>
                                                    <Text style={styles.videoAnalysisLabel}>{t('app.campaigns.videoAnalysis')}</Text>
                                                    <Text style={styles.videoAnalysisText} numberOfLines={2}>{post.video_analysis_summary}</Text>
                                                </View>
                                            )}
                                            {post.video_brand_presence && (
                                                <Text style={styles.brandPresenceText} numberOfLines={1}>{post.video_brand_presence}</Text>
                                            )}
                                        </View>
                                    );
                                })
                            )}
                        </View>
                    ))}
                </View>
            </View>
            </div>

        </ScrollView>
    );
};


const getStyles = () => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: THEME.bg,
    },
    contentContainer: {
        maxWidth: 1400,
        marginHorizontal: 'auto',
        width: '100%',
        padding: 32,
        paddingBottom: 60,
        gap: 20,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: THEME.bg,
        gap: 10,
    },
    loadingText: {
        color: THEME.textSecondary,
        marginTop: 16,
        fontSize: 16,
        fontWeight: '500',
    },
    loadingSubtext: {
        color: THEME.textTertiary,
        fontSize: 13,
    },
    errorText: {
        color: THEME.error,
        fontSize: 16,
        marginBottom: 16,
    },
    retryButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 10,
        backgroundColor: THEME.primary + '20',
    },
    retryText: {
        color: THEME.primary,
        fontWeight: '600',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        marginBottom: 8,
    },
    backBtn: {
        padding: 10,
        borderRadius: 10,
        backgroundColor: THEME.bgSurface,
    },
    pageTitle: {
        color: THEME.textMain,
        fontSize: 26,
        fontWeight: '800',
        letterSpacing: -0.4,
    },
    pageSubtitle: {
        color: THEME.textSecondary,
        fontSize: 14,
        marginTop: 2,
    },
    campaignHeaderRow: {
        flexDirection: 'row',
        gap: 12,
    },
    campaignHeaderCard: {
        flex: 1,
        backgroundColor: THEME.card,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 18,
        gap: 8,
    },
    campaignHeaderName: {
        color: THEME.textMain,
        fontSize: 16,
        fontWeight: '700',
        flex: 1,
    },
    campaignHeaderDesc: {
        color: THEME.textSecondary,
        fontSize: 13,
        lineHeight: 18,
    },
    statusPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8,
        alignSelf: 'flex-start',
    },
    sectionCard: {
        backgroundColor: THEME.card,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 24,
    },
    sectionTitle: {
        color: THEME.textMain,
        fontSize: 17,
        fontWeight: '700',
        marginBottom: 20,
    },
    metricLabel: {
        color: THEME.textSecondary,
        fontSize: 13,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    metricCampaignName: {
        color: THEME.textSecondary,
        fontSize: 12,
        width: 100,
        flexShrink: 0,
    },
    modeToggle: {
        flexDirection: 'row',
        backgroundColor: THEME.bgSurface,
        borderRadius: 10,
        padding: 3,
        gap: 2,
    },
    modeToggleBtn: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
    },
    modeToggleBtnActive: {
        backgroundColor: THEME.card,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
    },
    modeToggleBtnText: {
        color: THEME.textTertiary,
        fontSize: 12,
        fontWeight: '600',
    },
    modeToggleBtnTextActive: {
        color: THEME.textMain,
    },
    barTrack: {
        flex: 1,
        height: 8,
        borderRadius: 4,
        backgroundColor: THEME.bgSurface,
        overflow: 'hidden',
    },
    barFill: {
        height: '100%',
        borderRadius: 4,
        transition: 'width 0.5s ease',
    },
    platformBreakdownCard: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 14,
    },
    sentimentLabel: {
        color: THEME.textSecondary,
        fontSize: 12,
    },
    topPostsColumn: {
        gap: 10,
    },
    topPostItem: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 12,
        gap: 4,
    },
    topPostContent: {
        color: THEME.textSecondary,
        fontSize: 12,
        lineHeight: 17,
    },
    videoAnalysisBadge: {
        marginTop: 8,
        padding: 8,
        borderRadius: 8,
        backgroundColor: THEME.primary + '0D',
        borderLeftWidth: 2,
        borderLeftColor: THEME.primary + '60',
    },
    videoAnalysisLabel: {
        color: THEME.primary,
        fontSize: 10,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 3,
    },
    videoAnalysisText: {
        color: THEME.textSecondary,
        fontSize: 11,
        lineHeight: 16,
    },
    brandPresenceText: {
        color: THEME.textTertiary,
        fontSize: 11,
        fontStyle: 'italic',
        marginTop: 4,
    },
    // AI Insights Section
    aiWrapper: {
        backgroundColor: THEME.card,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 28,
        gap: 20,
    },
    aiWinnerBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        borderRadius: 14,
        padding: 18,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
    },
    aiWinnerTrophyWrap: {
        width: 48,
        height: 48,
        borderRadius: 14,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
    },
    aiWinnerLabel: {
        color: THEME.textSecondary,
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.8,
    },
    aiWinnerName: {
        fontSize: 20,
        fontWeight: '800',
        letterSpacing: -0.3,
        marginTop: 2,
    },
    aiSummaryBlock: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 12,
        padding: 16,
    },
    aiSummaryText: {
        color: THEME.textSecondary,
        fontSize: 14,
        lineHeight: 23,
    },
    aiSubsectionTitle: {
        color: THEME.textMain,
        fontSize: 15,
        fontWeight: '700',
        marginBottom: 8,
    },
    aiDiffChip: {
        flexDirection: 'row',
        gap: 0,
        backgroundColor: THEME.bgSurface,
        borderRadius: 10,
        overflow: 'hidden',
    },
    aiDiffAccent: {
        width: 3,
        alignSelf: 'stretch',
        flexShrink: 0,
    },
    aiDiffText: {
        color: THEME.textSecondary,
        fontSize: 13,
        lineHeight: 20,
        flex: 1,
        padding: 10,
        paddingLeft: 12,
    },
    aiStrengthCard: {
        backgroundColor: THEME.bgSurface,
        borderRadius: 12,
        overflow: 'hidden',
    },
    aiStrengthHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 10,
        paddingHorizontal: 14,
    },
    aiStrengthName: {
        color: THEME.textMain,
        fontSize: 14,
        fontWeight: '700',
        flex: 1,
    },
    aiStrengthPoint: {
        color: THEME.textSecondary,
        fontSize: 13,
        lineHeight: 20,
        flex: 1,
    },
    aiRecCard: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        backgroundColor: THEME.bgSurface,
        borderRadius: 12,
        padding: 14,
    },
    aiRecBadge: {
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: THEME.primary,
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
    },
    aiRecBadgeText: {
        color: '#000',
        fontSize: 12,
        fontWeight: '800',
    },
    aiRecText: {
        color: THEME.textSecondary,
        fontSize: 13,
        lineHeight: 20,
        flex: 1,
    },
    // -------------------------------------------------------------------------
    // Watch button (header)
    // -------------------------------------------------------------------------
    watchBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        backgroundColor: THEME.bgSurface,
    },
    watchBtnText: {
        color: THEME.textSecondary,
        fontSize: 13,
        fontWeight: '600',
    },
    watchingBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.success + '40',
        backgroundColor: THEME.success + '10',
    },
    watchingBadgeText: {
        color: THEME.success,
        fontSize: 13,
        fontWeight: '600',
    },
    watchingRemoveBtn: {
        marginLeft: 2,
        padding: 2,
    },
    // -------------------------------------------------------------------------
    // Watch Modal
    // -------------------------------------------------------------------------
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.3)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    watchModal: {
        width: '100%',
        maxWidth: 480,
        backgroundColor: THEME.modalBg,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        padding: 28,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    },
    watchModalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 18,
    },
    watchModalIconWrap: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: THEME.primary + '18',
        borderWidth: 1,
        borderColor: THEME.primary + '30',
        justifyContent: 'center',
        alignItems: 'center',
    },
    watchModalCloseBtn: {
        padding: 8,
        borderRadius: 10,
        backgroundColor: THEME.bgSurface,
    },
    watchModalTitle: {
        color: THEME.textMain,
        fontSize: 20,
        fontWeight: '800',
        letterSpacing: -0.3,
        marginBottom: 8,
    },
    watchModalSubtitle: {
        color: THEME.textSecondary,
        fontSize: 14,
        lineHeight: 22,
        marginBottom: 20,
    },
    watchCampaignPills: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 22,
    },
    watchCampaignPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 8,
        borderWidth: 1,
        backgroundColor: THEME.bgSurface,
        maxWidth: 200,
    },
    watchCampaignPillText: {
        color: THEME.textSecondary,
        fontSize: 12,
        fontWeight: '600',
    },
    watchNoteLabel: {
        color: THEME.textSecondary,
        fontSize: 13,
        fontWeight: '600',
        marginBottom: 8,
    },
    watchNoteInput: {
        backgroundColor: THEME.inputBg,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        color: THEME.textMain,
        fontSize: 14,
        lineHeight: 22,
        padding: 14,
        minHeight: 80,
        textAlignVertical: 'top',
        outlineStyle: 'none',
    },
    watchNoteCount: {
        color: THEME.textTertiary,
        fontSize: 11,
        textAlign: 'right',
        marginTop: 4,
        marginBottom: 20,
    },
    watchModalActions: {
        flexDirection: 'row',
        gap: 10,
    },
    watchCancelBtn: {
        flex: 1,
        paddingVertical: 13,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: THEME.cardBorder,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: THEME.bgSurface,
    },
    watchCancelBtnText: {
        color: THEME.textSecondary,
        fontSize: 15,
        fontWeight: '600',
    },
    watchConfirmBtn: {
        flex: 2,
        paddingVertical: 13,
        borderRadius: 8,
        backgroundColor: THEME.primary,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: 8,
    },
    watchConfirmBtnText: {
        color: '#fff',
        fontSize: 15,
        fontWeight: '700',
    },
});

let styles = getStyles();

const CampaignCompareScreen = () => <CampaignComparePanel />;

export default CampaignCompareScreen;
